import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import AppContext, {
  IDetailsListBasicExampleItem,
  IMessageBarAction,
  IMiniChartOptions,
} from "./components/AppContext";
import ShowQR from "./components/ShowQR";
import Asset from "./components/assets/Asset";
import Home from "./components/Home";
import { FC, useEffect, useState } from "react";
import ImportAsset from "./components/import/ImportAsset";
import Profile from "./components/Profile";
import { useIsAuthenticated as useIsAuthenticatedMSAL } from "@azure/msal-react";
import { DefaultButton, MessageBarType, PrimaryButton } from "@fluentui/react";
import { appRoles, loginRequest } from "./authConfig";
import ItemsList from "./components/assets/AssetsList";
import QrCodes from "./components/qr/QrCodes";
import Settings from "./components/settings/Settings";
import Issues from "./components/issues/Issues";
import Zones from "./components/zones/Zones";
import Categories from "./components/Categories";
import ImportIssueTypes from "./components/import/ImportIssueTypes";
import ImportZones from "./components/import/ImportZones";
import Issue from "./components/issues/Issue";
import Login from "./Login";
import PermissionsPivot from "./components/security/Security";
import ReportsViewer from "./components/reports/ReportsViewer";
import { Metadata } from "./components/settings/Metadata";
import AssetsChanges from "./components/assets/AssetsChanges";
import Tenant from "./components/security/Tenant";
import Group from "./components/security/Group";
import User from "./components/security/User";
import QrCodesTemplates from "./components/qr/QrCodesTemplates";
import ReferenceValue from "./components/settings/ReferenceValue";
import { RenderIf } from "./libs";
import Workflow from "./components/settings/Workflow";
import Workflows from "./components/settings/Workflows";
import Dashboard from "./components/dashboard/Dashboard";
import WorkOrdersV2 from "./components/workOrders/WorkOrdersV2";
import WorkOrder from "./components/workOrders/WorkOrder";
import ContractorsV2 from "./components/contractors/ContractorsV2";
import ContractorV2 from "./components/contractors/ContractorV2";
import Calendar from "./components/calendar/Calendar";
import Renters from "./components/renters/Renters";
import Renter from "./components/renters/Renter";
import Rents from "./components/renters/Rents";
import Rent from "./components/renters/Rent";
import WorkflowExecs from "./components/settings/WorkflowExecs";
import WorkflowExec from "./components/settings/WorkflowExec";
import Status from "./components/settings/Status";
import Report from "./components/settings/Report";
import { ApolloProvider } from "@apollo/client";
import { client } from "./services/apolloClient";

enum idps {
  AAD,
  DB,
}

let idp: idps = idps.AAD;

export { idps, idp };

const App: FC<any> = ({ instance }) => {

  const defaultItem: IDetailsListBasicExampleItem = {
    id: 0,
    uuid: "",
    code: "",
    name: "",
  };

  interface IMessage {
    message: string;
    messageType: MessageBarType;
    actions?: IMessageBarAction[]
  }

  const [selectedItem, setSelectedItem] =
    useState<IDetailsListBasicExampleItem>(defaultItem);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [message] = useState<IMessage>({ message: '', messageType: MessageBarType.info, actions: [] });
  const [selectedTab, setSelectedTab] = useState("");
  const [miniChartData, setMiniChartData] = useState<Map<string, number>>();
  const [miniChartOptions, setMiniChartOptions] = useState<IMiniChartOptions>();
  const context = {
    selectedItem: selectedItem,
    setSelectedItem: (item: IDetailsListBasicExampleItem) => {
      setSelectedItem(item);
    },
    isImportDialogOpen: isImportDialogOpen,
    setIsImportDialogOpen: (flag: boolean) => {
      setIsImportDialogOpen(flag);
    },
    isProfileDialogOpen: isProfileDialogOpen,
    setIsProfileDialogOpen: (flag: boolean) => {
      setIsProfileDialogOpen(flag);
    },
    isAuthenticated: isAuthenticated,
    setIsAuthenticated: (flag: boolean) => {
      setIsAuthenticated(flag);
    },
    actions: message.actions ?? [],
    setSelectedTab: (selectedTab: string, miniChartData?: Map<string, number>, miniChartOptions?: IMiniChartOptions) => {
      setSelectedTab(selectedTab);
      setMiniChartData(miniChartData ?? new Map<string, number>());
      setMiniChartOptions(miniChartOptions);
    },
    selectedTab: selectedTab,
    miniChartData: miniChartData ?? new Map<string, number>(),
    miniChartOptions: miniChartOptions ?? { xAxisFormat: (x: string) => x },
  };
  const [showLoginPage, setShowLoginPage] = useState(false);

  const handleSignIn = (mode: string) => {
    idp = mode === 'sso' ? idps.AAD : idps.DB;
    if (idp === idps.AAD) {
      instance.loginRedirect(loginRequest)
        .catch((e: any) => {
          console.error(e);
        });
      context.setIsAuthenticated(true);
    } else if (idp === idps.DB) {
      setShowLoginPage(true);
    }
  };

  const handleSignOut = () => {
    if (idp === idps.AAD) {
      instance.logoutRedirect().catch((e: any) => {
        console.error(e);
      });
    } else if (idp === idps.DB) {
      localStorage.removeItem('token');
      window.location.reload();
    }
  };

  // const accounts = instance.getAllAccounts();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const onLoad = async () => {
      if (idp === idps.AAD) {
        const currentAccount: any = instance.getActiveAccount();

        if (currentAccount && currentAccount.idTokenClaims["roles"]) {
          let intersection = [appRoles.Admin, appRoles.Viewer].filter((role: any) =>
            currentAccount.idTokenClaims["roles"].includes(role)
          );

          if (intersection.length > 0) {
            setIsAuthorized(true);
          }
        }
      } else {
        // to be completed for DB idp
        setIsAuthorized(true);
      }
    };
    onLoad();
  }, [instance]);

  const isAuthenticatedInAad = useIsAuthenticatedMSAL();

  return (
    <AppContext.Provider value={context}>
      <ApolloProvider client={client}>
        {(isAuthenticatedInAad || context.isAuthenticated) ? (
          <>
            <BrowserRouter basename="/">
              {isAuthorized ? (
                <>
                  <Home>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/assets" element={<ItemsList />} />
                      <Route path="/assetAudit" element={<AssetsChanges />} />
                      <Route path="/zones" element={<Zones />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/renters" element={<Renters />} />
                      <Route path="/newRenter" element={<Renter />} />
                      <Route path="/editRenter" element={<Renter />} />
                      <Route path="/rents" element={<Rents />} />
                      <Route path="/newRent" element={<Rent />} />
                      <Route path="/editRent" element={<Rent />} />
                      <Route path="/contractors" element={<ContractorsV2 />} />
                      <Route path="/newContractor" element={<ContractorV2 />} />
                      <Route path="/editContractor" element={<ContractorV2 />} />
                      <Route path="/newAsset" element={<Asset />} />
                      <Route path="/showQR" element={<ShowQR />} />
                      <Route path="/issues" element={<Issues />} />
                      <Route path="/newIssue/:by" element={<Issue />} />
                      <Route path="/editIssue" element={<Issue />} />
                      <Route path="/editAsset" element={<Asset />} />
                      <Route path="/importAssets" element={<ImportAsset />} />
                      <Route path="/importIssueTypes" element={<ImportIssueTypes />} />
                      <Route path="/importZones" element={<ImportZones />} />
                      <Route path="/workOrders" element={<WorkOrdersV2 />} />
                      <Route path="/newWorkOrder" element={<WorkOrder />} />
                      <Route path="/editWorkOrder" element={<WorkOrder />} />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/qrCodes" element={<QrCodes />} />
                      <Route path="/qrCodeTemplates" element={<QrCodesTemplates />} />
                      <Route path="/security" element={<PermissionsPivot />} />
                      <Route path="/newTenant" element={<Tenant />} />
                      <Route path="/editTenant" element={<Tenant />} />
                      <Route path="/newGroup" element={<Group />} />
                      <Route path="/editGroup" element={<Group />} />
                      <Route path="/newUser" element={<User />} />
                      <Route path="/editUser" element={<User />} />
                      <Route path="/workflows" element={<Workflows />} />
                      <Route path="/workflows/:tab" element={<Workflows />} />
                      <Route path="/newWorkflow" element={<Workflow />} />
                      <Route path="/editWorkflow" element={<Workflow />} />
                      <Route path="/workflowExecs" element={<WorkflowExecs />} />
                      <Route path="/showWorkflowExec" element={<WorkflowExec />} />
                      <Route path="/metadata" element={<Metadata />} />
                      <Route path="/settings/:tab" element={<Settings />} />
                      <Route path="/newStatuses/:type" element={<Status />} />
                      <Route path="/editStatuses/:type" element={<Status />} />
                      <Route path="/newRefValue" element={<ReferenceValue />} />
                      <Route path="/editRefValue" element={<ReferenceValue />} />
                      <Route path="/reports" element={<ReportsViewer />} />
                      <Route path="/newReport" element={<Report />} />
                      <Route path="/editReport" element={<Report />} />
                    </Routes>
                  </Home>
                </>
              ) : (
                <div className="App">
                  <p>
                    Your account is not permitted to use this application. Contact
                    administrator or try another account.
                  </p>
                  <PrimaryButton onClick={handleSignOut}>Sign out</PrimaryButton>
                </div>
              )}
            </BrowserRouter>
            <Profile />
          </>) : (
          <>
            {showLoginPage ? <Login onLoginSuccess={() => {
              setIsAuthenticated(true);
              if (idp === idps.DB) {
                setIsAuthorized(true);
              }
            }} /> :
              <div className="App">
                <p>You are not signed in! Please sign in.</p>
                <PrimaryButton onClick={() => handleSignIn('sso')}>Organization Sign In</PrimaryButton>
                <RenderIf condition={!!process.env.REACT_APP_SUPPORT_IDP_DB}>
                  <DefaultButton onClick={() => handleSignIn('db')}>Sign In</DefaultButton>
                </RenderIf>
              </div>}
          </>)}
      </ApolloProvider>
    </AppContext.Provider>
  );
};

export default App;
